import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Route } from "lucide-react";
import { getRoutesReport } from "@/api/reports";
import type { TFunction } from "i18next";
import {
  formatCurrency,
  formatDistanceKm,
  formatDuration,
  formatNumber,
} from "@/lib/format";
import { useCostUnit } from "@/hooks/useCostUnit";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";
import { cn } from "@/lib/cn";
import type { ReportParams, RouteReportRow } from "@/types/reports";

interface Props {
  params: ReportParams;
  visibleKeys: string[];
}

const PAGE_SIZE = 20;
const SKELETON_ROWS = 5;

function otdClass(v: number | null): string {
  if (v === null) return "text-gray-400";
  if (v >= 90) return "text-emerald-600 font-semibold";
  if (v >= 70) return "text-amber-600 font-semibold";
  return "text-rose-600 font-semibold";
}

function formatDurationCell(mins: number | null, t: TFunction): string {
  if (mins === null) return "—";
  return formatDuration(mins, t);
}

function renderRouteCell(
  key: string,
  row: RouteReportRow,
  t: TFunction,
  costUnit: string,
  locale: string,
): React.ReactNode {
  switch (key) {
    case "routeName":
      return (
        <span
          className="line-clamp-2 block min-w-[12rem] max-w-[18rem] font-semibold text-gray-900"
          title={row.route_name}
        >
          {row.route_name}
        </span>
      );
    case "date":
      return <span className="text-gray-500">{row.date}</span>;
    case "vehicle":
      return row.vehicle_number != null ? (
        <span className="text-gray-500">{row.vehicle_number}</span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    case "driver":
      return row.driver_name != null ? (
        <span className="text-gray-500">{row.driver_name}</span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    case "plannedKm":
      return row.planned_mileage_km != null ? (
        <span className="font-medium text-gray-700">
          {formatDistanceKm(row.planned_mileage_km)}
        </span>
      ) : (
        <span
          className="text-gray-300"
          title={t("routes.distanceNotCalculated")}
        >
          —
        </span>
      );
    case "actualKm":
      return row.distance_actual_km != null ? (
        <span className="font-medium text-gray-700">
          {formatDistanceKm(row.distance_actual_km)}
        </span>
      ) : (
        <span
          className="text-gray-300"
          title={t("routes.distanceNotCalculated")}
        >
          —
        </span>
      );
    case "plannedDur":
      return (
        <span className="text-gray-500">
          {formatDurationCell(row.planned_duration_min, t)}
        </span>
      );
    case "actualDur":
      return (
        <span className="text-gray-500">
          {formatDurationCell(row.actual_duration_min, t)}
        </span>
      );
    case "stops":
      return (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-sm font-medium tabular-nums text-gray-700">
            {formatNumber(row.stops_total)}
          </span>
          <span
            className="inline-flex items-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700"
            title={t("reports.stops.confirmed")}
          >
            {row.stops_confirmed}
          </span>
          <span
            className="inline-flex items-center rounded-full bg-rose-100 px-1.5 py-0.5 text-xs font-medium text-rose-700"
            title={t("reports.stops.rejected")}
          >
            {row.stops_rejected}
          </span>
          <span
            className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600"
            title={t("reports.stops.skipped")}
          >
            {row.stops_skipped}
          </span>
        </div>
      );
    case "otd":
      return (
        <span className={otdClass(row.otd_percent)}>
          {row.otd_percent != null ? `${row.otd_percent}%` : "—"}
        </span>
      );
    case "cost":
      return row.route_cost != null ? (
        <span className="font-medium text-gray-700">
          {formatCurrency(Number(row.route_cost), costUnit, locale)}
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    case "utilWeight":
      return row.utilization_weight_pct != null ? (
        <span className="font-medium text-gray-700">
          {formatNumber(row.utilization_weight_pct)}%
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    case "utilVolume":
      return row.utilization_volume_pct != null ? (
        <span className="font-medium text-gray-700">
          {formatNumber(row.utilization_volume_pct)}%
        </span>
      ) : (
        <span className="text-gray-300">—</span>
      );
    default:
      return null;
  }
}

export function RoutesReportTable({ params, visibleKeys }: Props) {
  const { t, i18n } = useTranslation();
  const costUnit = useCostUnit();
  const [page, setPage] = useState(1);

  // `numeric` right-aligns the column header + cells with tabular-nums
  // (TASK-3840). Text columns (route name, date, vehicle, driver) and the
  // multi-badge `stops` widget stay left-aligned.
  const COLUMNS: { key: string; label: string; numeric?: boolean }[] = [
    { key: "routeName", label: t("reports.routesTable.routeName") },
    { key: "date", label: t("reports.routesTable.date") },
    { key: "vehicle", label: t("reports.routesTable.vehicle") },
    { key: "driver", label: t("reports.routesTable.driver") },
    {
      key: "plannedKm",
      label: t("reports.routesTable.plannedKm"),
      numeric: true,
    },
    {
      key: "actualKm",
      label: t("reports.routesTable.actualKm"),
      numeric: true,
    },
    {
      key: "plannedDur",
      label: t("reports.routesTable.plannedDur"),
      numeric: true,
    },
    {
      key: "actualDur",
      label: t("reports.routesTable.actualDur"),
      numeric: true,
    },
    { key: "stops", label: t("reports.routesTable.stops") },
    { key: "otd", label: t("reports.routesTable.otd"), numeric: true },
    { key: "cost", label: t("reports.routesTable.cost"), numeric: true },
    {
      key: "utilWeight",
      label: t("reports.routesTable.utilWeight"),
      numeric: true,
    },
    {
      key: "utilVolume",
      label: t("reports.routesTable.utilVolume"),
      numeric: true,
    },
  ];

  const visibleCols = COLUMNS.filter((col) => visibleKeys.includes(col.key));

  useEffect(() => {
    setPage(1);
  }, [params]);

  const { data, isLoading } = useQuery({
    queryKey: ["reports", "routes", params, page],
    queryFn: () => getRoutesReport({ ...params, page }),
  });

  const rows = data?.results ?? [];
  const totalPages = data ? Math.ceil(data.count / PAGE_SIZE) : 1;

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/60">
              {visibleCols.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "px-5 py-3.5 text-xs font-semibold uppercase tracking-widest text-gray-400",
                    col.numeric ? "text-right" : "text-left",
                  )}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0">
                    {Array.from({ length: visibleCols.length }).map((_, j) => (
                      <td key={j} className="px-5 py-4">
                        <Skeleton className="h-4 w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              : rows.map((row) => (
                  <tr
                    key={row.route_id}
                    className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50"
                  >
                    {visibleCols.map((col) => (
                      <td
                        key={col.key}
                        className={cn(
                          "px-5 py-4",
                          col.numeric && "text-right tabular-nums",
                        )}
                      >
                        {renderRouteCell(
                          col.key,
                          row,
                          t,
                          costUnit,
                          i18n.language,
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      {!isLoading && rows.length === 0 && (
        <EmptyState icon={Route} title={t("reports.routesTable.noData")} />
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
          <span className="text-xs text-gray-400">
            {t("reports.pagination.showing", {
              from: (page - 1) * PAGE_SIZE + 1,
              to: Math.min(page * PAGE_SIZE, data?.count ?? 0),
              total: data?.count ?? 0,
            })}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("common.previous")}
            </button>
            <button
              type="button"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t("common.next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
